import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Minimize2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import './WhatsInTheBoxGame.css';

const WhatsInTheBoxGame = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const gameId = searchParams.get('gameId');
  const iframeRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showToolbar, setShowToolbar] = useState(false);
  const containerRef = useRef(null);
  const toolbarTimeoutRef = useRef(null);

  const saveGame = (gameData) => {
    try {
      const data = localStorage.getItem('whatsInTheBoxDatabase');
      const games = data ? JSON.parse(data) : [];
      
      const newGame = {
        id: gameData.id || Date.now().toString(),
        title: gameData.title || 'Mystery Box',
        items: gameData.items || [],
        createdAt: gameData.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Check if game already exists
      const existingIndex = games.findIndex(g => g.id === newGame.id);
      if (existingIndex >= 0) {
        games[existingIndex] = newGame;
      } else {
        games.push(newGame);
      }

      localStorage.setItem('whatsInTheBoxDatabase', JSON.stringify(games));
      toast.success('Game saved successfully!');
    } catch (error) {
      console.error('Error saving game:', error);
      toast.error('Failed to save game');
    }
  };

  const exitFullscreenAndNavigate = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      }
    } catch (error) {
      console.log('Exit fullscreen failed:', error);
    }
    navigate('/whats-in-the-box-hub');
  };

  const exitFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (error) {
      console.log('Exit fullscreen failed:', error);
    }
  };

  // Auto-enter fullscreen on mount
  useEffect(() => {
    const enterFullscreen = async () => {
      try {
        if (containerRef.current && !document.fullscreenElement) {
          await containerRef.current.requestFullscreen();
          setIsFullscreen(true);
        }
      } catch (error) {
        console.log('Fullscreen request failed:', error);
      }
    };

    // Small delay to ensure component is mounted
    const timer = setTimeout(enterFullscreen, 300);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // If we have a gameId, load the game data and send it to the iframe
    if (gameId) {
      const data = localStorage.getItem('whatsInTheBoxDatabase');
      const games = data ? JSON.parse(data) : [];
      const game = games.find(g => g.id === gameId);
      
      if (game && iframeRef.current) {
        // Wait for iframe to load, then send the game data
        const iframe = iframeRef.current;
        iframe.onload = () => {
          iframe.contentWindow.postMessage({
            type: 'LOAD_GAME',
            game: game
          }, '*');
        };
      }
    }

    // Listen for messages from the iframe
    const handleMessage = (event) => {
      if (event.data.type === 'SAVE_GAME') {
        saveGame(event.data.game);
      } else if (event.data.type === 'EXIT_GAME') {
        exitFullscreenAndNavigate();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [gameId, navigate]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Handle toolbar visibility - show on hover at top edge
  const handleToolbarAreaHover = () => {
    setShowToolbar(true);
    
    // Clear existing timeout
    if (toolbarTimeoutRef.current) {
      clearTimeout(toolbarTimeoutRef.current);
    }
  };

  const handleToolbarMouseEnter = () => {
    if (toolbarTimeoutRef.current) {
      clearTimeout(toolbarTimeoutRef.current);
    }
    setShowToolbar(true);
  };

  const handleToolbarMouseLeave = () => {
    toolbarTimeoutRef.current = setTimeout(() => {
      setShowToolbar(false);
    }, 1000);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (toolbarTimeoutRef.current) {
        clearTimeout(toolbarTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="whats-in-box-game-page" ref={containerRef}>
      {/* Small trigger area at top edge - hover to show toolbar */}
      <div 
        className="toolbar-trigger-area"
        onMouseEnter={handleToolbarAreaHover}
      />
      
      {/* Transparent auto-hide toolbar */}
      <div 
        className={`game-toolbar ${showToolbar ? 'visible' : 'hidden'}`}
        onMouseEnter={handleToolbarMouseEnter}
        onMouseLeave={handleToolbarMouseLeave}
      >
        <Button 
          onClick={exitFullscreenAndNavigate} 
          className="back-button"
          variant="ghost"
        >
          <ArrowLeft size={20} />
          Back to Hub
        </Button>
        <div className="toolbar-title">
          <span className="toolbar-icon">🕵️</span>
          <span>ماذا يوجد داخل الصندوق</span>
        </div>
        <div className="toolbar-actions">
          {isFullscreen && (
            <Button onClick={exitFullscreen} variant="ghost" className="fullscreen-btn">
              <Minimize2 size={20} />
              Exit Fullscreen
            </Button>
          )}
        </div>
      </div>
      
      <div className="game-container">
        <iframe
          ref={iframeRef}
          src="/whats-in-the-box.html"
          title="What's in the Box Game"
          className="game-iframe"
          allow="fullscreen"
        />
      </div>
    </div>
  );
};

export default WhatsInTheBoxGame;
